'use client';

import { cn } from '@/lib/utils';
import type { BoardSortMode } from './tile-layout';

interface BoardSortToggleProps {
  mode: BoardSortMode;
  onChange: (m: BoardSortMode) => void;
  /** `3d` — skewed “lying on board” chip; `2d` — flat strip for Lite canvas */
  variant: '2d' | '3d';
  className?: string;
}

export function BoardSortToggle({ mode, onChange, variant, className }: BoardSortToggleProps) {
  const is3d = variant === '3d';
  return (
    <div
      role="group"
      aria-label="Board tile ordering"
      className={cn(
        'flex items-center gap-0.5 p-0.5 rounded-[4px]',
        'bg-black/70 backdrop-blur-md border border-amber-500/30 shadow-[inset_0_1px_0_rgba(255,215,120,0.12)]',
        is3d &&
          'origin-center [transform:perspective(420px)_rotateX(-14deg)] shadow-[0_10px_24px_-8px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,215,120,0.15)]',
        className,
      )}
    >
      <SortBtn
        active={mode === 'floor'}
        onClick={() => onChange('floor')}
        label="Price"
        title="Order by price — lowest first"
      />
      <SortBtn
        active={mode === 'time'}
        onClick={() => onChange('time')}
        label="Time"
        title="Order by runway — shortest time left first"
      />
    </div>
  );
}

function SortBtn({
  active,
  onClick,
  label,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  title: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'px-2 py-1 rounded-[3px] text-[9px] font-mono uppercase tracking-[0.12em] transition-colors',
        active
          ? 'bg-amber-500/20 text-amber-100 shadow-[inset_0_0_0_1px_rgba(255,215,120,0.35)]'
          : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5',
      )}
    >
      {label}
    </button>
  );
}
